import { z } from 'zod/v4';

import { OffTheShelfOfferOriginEnumRuntype } from '../offer/offerBackendTypes';
import { OfferImportContextOriginRuntype } from '../offerImportContext/offerImportContextBackendTypes';

// Mirrors domain-sourcing-scenario-types::ProjectIdDto, serde(tag = "type", content = "id").
export const SourcingContextRuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('Rfq'), id: z.string() }),
    z.object({ type: z.literal('NegotiationV2'), id: z.string() }),
]);

export type SourcingContext = z.infer<typeof SourcingContextRuntype>;

/** @deprecated Use `SourcingContext` instead. */
export type ProjectIdDTO = SourcingContext;

export const QuoteRequestSelectorDTORuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('ById'), data: z.string().uuid() }),
    z.object({ type: z.literal('ByProject'), data: SourcingContextRuntype }),
    z.object({ type: z.literal('BySupplier'), data: z.string().uuid() }),
]);

export const ApiSourceSelectorDTORuntype = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('ByApiSource'),
        data: OffTheShelfOfferOriginEnumRuntype,
    }),
]);

export const OfferImportContextSelectorDTORuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('ById'), data: z.string().uuid() }),
    z.object({ type: z.literal('ByOrigin'), data: OfferImportContextOriginRuntype }),
]);

// Single-variant enums, so `All` is the only selector: excluding everything is an `AnyOf: [All]` exclude list.
export const ManualOfferSelectorDTORuntype = z.discriminatedUnion('type', [z.object({ type: z.literal('All') })]);

export const NoContextImportOfferSelectorDTORuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('All') }),
]);

export type QuoteRequestSelectorDTO = z.infer<typeof QuoteRequestSelectorDTORuntype>;

export type ApiSourceSelectorDTO = z.infer<typeof ApiSourceSelectorDTORuntype>;

export type OfferImportContextSelectorDTO = z.infer<typeof OfferImportContextSelectorDTORuntype>;

export type ManualOfferSelectorDTO = z.infer<typeof ManualOfferSelectorDTORuntype>;

export type NoContextImportOfferSelectorDTO = z.infer<typeof NoContextImportOfferSelectorDTORuntype>;

export const IncludeQuoteRequestDTORuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('All') }),
    z.object({
        type: z.literal('AnyOf'),
        data: z.array(QuoteRequestSelectorDTORuntype),
    }),
]);

export const IncludeApiSourceDTORuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('All') }),
    z.object({
        type: z.literal('AnyOf'),
        data: z.array(ApiSourceSelectorDTORuntype),
    }),
]);

export const IncludeOfferImportContextDTORuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('All') }),
    z.object({
        type: z.literal('AnyOf'),
        data: z.array(OfferImportContextSelectorDTORuntype),
    }),
]);

export const IncludeManualOfferDTORuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('All') }),
    z.object({
        type: z.literal('AnyOf'),
        data: z.array(ManualOfferSelectorDTORuntype),
    }),
]);

export const IncludeNoContextImportOfferDTORuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('All') }),
    z.object({
        type: z.literal('AnyOf'),
        data: z.array(NoContextImportOfferSelectorDTORuntype),
    }),
]);

export type IncludeQuoteRequestDTO = z.infer<typeof IncludeQuoteRequestDTORuntype>;
export type IncludeApiSourceDTO = z.infer<typeof IncludeApiSourceDTORuntype>;
export type IncludeOfferImportContextDTO = z.infer<typeof IncludeOfferImportContextDTORuntype>;
export type IncludeManualOfferDTO = z.infer<typeof IncludeManualOfferDTORuntype>;
export type IncludeNoContextImportOfferDTO = z.infer<typeof IncludeNoContextImportOfferDTORuntype>;

export const ExcludeQuoteRequestDTORuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('None') }),
    z.object({
        type: z.literal('AnyOf'),
        data: z.array(QuoteRequestSelectorDTORuntype),
    }),
]);

export const ExcludeApiSourceDTORuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('None') }),
    z.object({
        type: z.literal('AnyOf'),
        data: z.array(ApiSourceSelectorDTORuntype),
    }),
]);

export const ExcludeOfferImportContextDTORuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('None') }),
    z.object({
        type: z.literal('AnyOf'),
        data: z.array(OfferImportContextSelectorDTORuntype),
    }),
]);

export const ExcludeManualOfferDTORuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('None') }),
    z.object({
        type: z.literal('AnyOf'),
        data: z.array(ManualOfferSelectorDTORuntype),
    }),
]);

export const ExcludeNoContextImportOfferDTORuntype = z.discriminatedUnion('type', [
    z.object({ type: z.literal('None') }),
    z.object({
        type: z.literal('AnyOf'),
        data: z.array(NoContextImportOfferSelectorDTORuntype),
    }),
]);

export type ExcludeQuoteRequestDTO = z.infer<typeof ExcludeQuoteRequestDTORuntype>;

export type ExcludeApiSourceDTO = z.infer<typeof ExcludeApiSourceDTORuntype>;

export type ExcludeOfferImportContextDTO = z.infer<typeof ExcludeOfferImportContextDTORuntype>;

export type ExcludeManualOfferDTO = z.infer<typeof ExcludeManualOfferDTORuntype>;

export type ExcludeNoContextImportOfferDTO = z.infer<typeof ExcludeNoContextImportOfferDTORuntype>;

export const QuoteRequestSelectionDTORuntype = z.object({
    include: IncludeQuoteRequestDTORuntype,
    exclude: ExcludeQuoteRequestDTORuntype,
});

export const ApiSourceSelectionDTORuntype = z.object({
    include: IncludeApiSourceDTORuntype,
    exclude: ExcludeApiSourceDTORuntype,
});

export const OfferImportContextSelectionDTORuntype = z.object({
    include: IncludeOfferImportContextDTORuntype,
    exclude: ExcludeOfferImportContextDTORuntype,
});

export const ManualOfferSelectionDTORuntype = z.object({
    include: IncludeManualOfferDTORuntype,
    exclude: ExcludeManualOfferDTORuntype,
});

export const NoContextImportOfferSelectionDTORuntype = z.object({
    include: IncludeNoContextImportOfferDTORuntype,
    exclude: ExcludeNoContextImportOfferDTORuntype,
});

export type QuoteRequestSelectionDTO = z.infer<typeof QuoteRequestSelectionDTORuntype>;

export type ApiSourceSelectionDTO = z.infer<typeof ApiSourceSelectionDTORuntype>;

export type OfferImportContextSelectionDTO = z.infer<typeof OfferImportContextSelectionDTORuntype>;

export type ManualOfferSelectionDTO = z.infer<typeof ManualOfferSelectionDTORuntype>;

export type NoContextImportOfferSelectionDTO = z.infer<typeof NoContextImportOfferSelectionDTORuntype>;

export const DataSourcesDTORuntype = z.object({
    quote_requests: QuoteRequestSelectionDTORuntype,
    api_sources: ApiSourceSelectionDTORuntype,
    // Backends before MR 21673 (offer-import-context data source) lack this field, so default to selecting all.
    offer_import_contexts: OfferImportContextSelectionDTORuntype.default({
        include: { type: 'All' },
        exclude: { type: 'None' },
    }),
    // Backends predating MR 22224 don't return these two either.
    manual_offers: ManualOfferSelectionDTORuntype.default({
        include: { type: 'All' },
        exclude: { type: 'None' },
    }),
    no_context_import_offers: NoContextImportOfferSelectionDTORuntype.default({
        include: { type: 'All' },
        exclude: { type: 'None' },
    }),
});

export type DataSourcesDTO = z.infer<typeof DataSourcesDTORuntype>;

export const DEFAULT_DATA_SOURCES: DataSourcesDTO = {
    quote_requests: {
        include: { type: 'All' },
        exclude: { type: 'None' },
    },

    api_sources: {
        include: { type: 'All' },
        exclude: { type: 'None' },
    },

    offer_import_contexts: {
        include: { type: 'All' },
        exclude: { type: 'None' },
    },

    manual_offers: {
        include: { type: 'All' },
        exclude: { type: 'None' },
    },

    no_context_import_offers: {
        include: { type: 'All' },
        exclude: { type: 'None' },
    },
};
